import { useState, type FormEvent } from 'react';

const SUBMIT_ADDR = 'https://readdy.ai/api/form/db7n4datp9sllopf7i0g';

const topics = ['内容纠错', '功能建议', '版权问题', '商务合作', '其他'];

export default function FeedbackForm() {
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');
  const [messageLen, setMessageLen] = useState(0);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const payload = new URLSearchParams();

    // honeypot: bots fill it, real users never see it
    const honey = String(formData.get('website_alt') || '').trim();
    if (honey) {
      setStatus('success');
      form.reset();
      setMessageLen(0);
      return;
    }

    formData.forEach((value, key) => {
      if (key === 'website_alt') return;
      const str = String(value).trim();
      if (str) payload.append(key, str);
    });

    setStatus('loading');
    setFormError('');

    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: payload.toString(),
      });
      const responseText = await response.text();
      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.meta?.detail || parsed?.message || responseText || '';
      const isSpam = /spam/i.test(serverMsg);
      const okCode = parsed?.code === 'OK';

      if (response.ok && okCode && !isSpam) {
        setStatus('success');
        form.reset();
        setMessageLen(0);
      } else {
        setStatus('error');
        setFormError(serverMsg || '提交失败，请稍后重试');
      }
    } catch {
      setStatus('error');
      setFormError('网络异常，提交失败，请稍后重试');
    }
  };

  return (
    <form
      id="hanman-feedback-form"
      data-readdy-form
      action={SUBMIT_ADDR}
      method="post"
      onSubmit={handleSubmit}
      className="rounded-lg border border-background-200/80 bg-background-50 p-5 md:p-6"
    >
      <h2 className="flex items-center gap-2 text-lg font-bold text-foreground-950">
        <i className="ri-feedback-line text-primary-500" />
        意见反馈
      </h2>
      <p className="mt-1 text-sm text-foreground-500">
        告诉我们你的想法。当前为前端演示收集，暂不支持后台处理，敬请谅解。
      </p>

      <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <label htmlFor="fb-name" className="mb-1.5 block text-xs text-foreground-600">
            你的称呼
          </label>
          <input
            id="fb-name"
            name="name"
            type="text"
            placeholder="请输入称呼"
            className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-800 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
          />
        </div>
        <div>
          <label htmlFor="fb-email" className="mb-1.5 block text-xs text-foreground-600">
            联系邮箱
          </label>
          <input
            id="fb-email"
            name="email"
            type="email"
            required
            placeholder="you@example.com"
            className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-800 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
          />
        </div>
      </div>

      <div className="mt-4">
        <label htmlFor="fb-type" className="mb-1.5 block text-xs text-foreground-600">
          反馈类型
        </label>
        <select
          id="fb-type"
          name="type"
          defaultValue={topics[0]}
          className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-800 focus:border-primary-400 focus:outline-none cursor-pointer"
        >
          {topics.map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </select>
      </div>

      <div className="mt-4">
        <label htmlFor="fb-message" className="mb-1.5 block text-xs text-foreground-600">
          反馈内容
        </label>
        <textarea
          id="fb-message"
          name="message"
          required
          maxLength={500}
          rows={5}
          onChange={(e) => setMessageLen(e.target.value.length)}
          placeholder="请描述你遇到的问题或建议（最多 500 字）"
          className="w-full resize-none rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-800 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
        />
        <p className="mt-1 text-right text-[11px] text-foreground-400">{messageLen}/500</p>
      </div>

      <input
        type="text"
        name="website_alt"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        readOnly
        className="form-antispam-field"
      />

      {status === 'error' && (
        <p className="mt-3 flex items-center gap-1.5 rounded-md bg-accent-100 px-3 py-2 text-sm text-accent-800">
          <i className="ri-error-warning-line" />
          {formError}
        </p>
      )}
      {status === 'success' && (
        <p className="mt-3 flex items-center gap-1.5 rounded-md bg-secondary-100 px-3 py-2 text-sm text-secondary-900">
          <i className="ri-checkbox-circle-line" />
          感谢反馈！我们已收到你的信息。
        </p>
      )}

      <button
        type="submit"
        disabled={status === 'loading'}
        className="mt-5 flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:opacity-60 cursor-pointer"
      >
        <i className="ri-send-plane-line" />
        {status === 'loading' ? '提交中…' : '提交反馈'}
      </button>
    </form>
  );
}